<!-- ========================================== -->
<!-- PaymentNotificationBadge.vue               -->
<!-- layouts/PaymentNotificationBadge.vue       -->
<!-- ========================================== -->

<template>
  <div v-if="shouldShowButton">

    <!-- FAB + Badge -->
    <div class="notification-trigger-wrapper">
      <v-tooltip right>
        <template v-slot:activator="{ on }">
          <v-btn
            fab
            color="teal darken-1"
            dark
            class="notification-trigger"
            @click="openDialog"
            v-on="on"
          >
            <i class="fas fa-wallet"></i>
          </v-btn>
        </template>
        <span>
          <strong v-if="notificationCount > 0">
            {{ notificationCount }} Njangi payout{{ notificationCount > 1 ? 's' : '' }} received
          </strong>
          <span v-else>Njangi Payouts</span>
        </span>
      </v-tooltip>

      <span v-if="notificationCount > 0" class="custom-badge">{{ notificationCount }}</span>
    </div>

    <!-- ─── DIALOG ──────────────────────────────────────────── -->
    <v-dialog v-model="showDialog" max-width="520px" persistent>
      <v-card v-if="currentPayment" class="payout-dialog" style="border-radius: 20px; overflow: hidden;">

        <!-- ── Hero Header ──────────────────────────────────── -->
        <div class="dialog-hero">
          <div class="dialog-hero-glow"></div>

          <v-layout row align-center class="hero-inner">

            <!-- Icon circle -->
            <div class="hero-icon-wrap mr-4">
              <div class="hero-pulse"></div>
              <v-icon size="30" color="white">fas fa-coins</v-icon>
            </div>

            <!-- Texts -->
            <div class="hero-text" style="flex: 1;">
              <div class="hero-eyebrow">Njangi Payout</div>
              <h2 class="hero-title">Payout received</h2>
              <div class="hero-chip">
                <span class="dot-live"></span>
                Just confirmed
              </div>
            </div>

            <!-- (Close button removed: dialog cannot be closed until acknowledged) -->

          </v-layout>
        </div>

        <!-- ── Body ─────────────────────────────────────────── -->
        <v-card-text class="pa-4" style="max-height: 480px; overflow-y: auto;">

          <!-- KPI Row -->
          <v-layout row wrap class="mb-4">
            <v-flex xs12 sm4 class="pa-1">
              <div class="kpi-card kpi-teal">
                <v-icon size="24" color="teal darken-2" class="mb-1">fas fa-money-bill-wave</v-icon>
                <span class="kpi-label">Amount</span>
                <span class="kpi-value teal--text text--darken-2">
                  {{ fmt(currentPayment.amount_paid) }} FCFA
                </span>
              </div>
            </v-flex>
            <v-flex xs12 sm4 class="pa-1">
              <div class="kpi-card kpi-green">
                <v-icon size="24" color="green darken-2" class="mb-1">fas fa-users</v-icon>
                <span class="kpi-label">Group</span>
                <span class="kpi-value green--text text--darken-2">
                  {{ currentPayment.group_name }}
                </span>
              </div>
            </v-flex>
            <v-flex xs12 sm4 class="pa-1">
              <div class="kpi-card kpi-purple">
                <v-icon size="24" color="purple darken-2" class="mb-1">fas fa-calendar-alt</v-icon>
                <span class="kpi-label">Session</span>
                <span class="kpi-value purple--text text--darken-2">
                  #{{ currentPayment.session_number }}
                </span>
              </div>
            </v-flex>
          </v-layout>

          <!-- Detail rows -->
          <div class="detail-section mb-4">

            <div class="detail-row">
              <v-icon small color="grey lighten-1" class="detail-icon">fas fa-mobile-alt</v-icon>
              <span class="detail-label">Paid via</span>
              <span class="detail-value">{{ currentPayment.payment_method }}</span>
            </div>

            <v-divider></v-divider>

            <div v-if="currentPayment.session_start_date && currentPayment.session_end_date" class="detail-row">
              <v-icon small color="grey lighten-1" class="detail-icon">fas fa-calendar-alt</v-icon>
              <span class="detail-label">Session Period</span>
              <span class="detail-value">{{ currentPayment.session_start_date }} → {{ currentPayment.session_end_date }}</span>
            </div>

            <v-divider v-if="currentPayment.session_start_date && currentPayment.session_end_date"></v-divider>

            <div class="detail-row">
              <v-icon small color="grey lighten-1" class="detail-icon">fas fa-clock</v-icon>
              <span class="detail-label">Date Received</span>
              <span class="detail-value">{{ currentPayment.paid_at }}</span>
            </div>

            <v-divider v-if="currentPayment.notes"></v-divider>

            <div v-if="currentPayment.notes" class="detail-row">
              <v-icon small color="grey lighten-1" class="detail-icon">fas fa-sticky-note</v-icon>
              <span class="detail-label">Notes</span>
              <span class="detail-value" style="font-style: italic;">{{ currentPayment.notes }}</span>
            </div>

          </div>

          <!-- Note box -->
          <div class="note-box">
            <v-icon small color="orange darken-2" class="mr-2 mt-1">fas fa-exclamation-circle</v-icon>
            <div>
              Verify your mobile money wallet to confirm receipt of the funds.
              Tap <strong>Acknowledge</strong> once confirmed.
            </div>
          </div>

          <!-- Multi-payout counter -->
          <div v-if="notificationCount > 1" class="multi-badge mt-3">
            <v-icon small color="teal darken-2" class="mr-1">fas fa-layer-group</v-icon>
            {{ notificationCount - 1 }} more payout{{ notificationCount - 1 > 1 ? 's' : '' }} waiting
          </div>

        </v-card-text>

        <!-- ── Actions ───────────────────────────────────────── -->
        <v-card-actions class="dialog-actions pa-4">
          <v-btn
            block
            large
            color="teal darken-2"
            dark
            @click="acknowledgePayment"
            :loading="acknowledging"
            class="action-primary"
          >
            <v-icon left small>fas fa-check-circle</v-icon>
            Acknowledge
          </v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'PaymentNotificationBadge',

  data() {
    return {
      notificationCount: 0,
      unreadPayments:    [],
      refreshInterval:   null,
      showDialog:        false,
      acknowledging:     false
    }
  },

  computed: {
    shouldShowButton() {
      return this.notificationCount > 0
    },
    currentPayment() {
      return this.unreadPayments.length > 0 ? this.unreadPayments[0] : null
    }
  },

  mounted() {
    console.log('🚀 [PaymentNotificationBadge] Mounted successfully and initiating 15s poll interval.');
    this.loadNotifications()
    this.refreshInterval = setInterval(this.loadNotifications, 15000)
  },

  beforeDestroy() {
    if (this.refreshInterval) clearInterval(this.refreshInterval)
  },

  methods: {
    fmt(val) {
      if (val === null || val === undefined || val === '') return '—'
      const n = Number(val)
      return isNaN(n) ? '—' : n.toLocaleString('fr-CM')
    },

    async loadNotifications() {
      console.log('📡 [PaymentNotificationBadge] Querying `/api/v1/njangi/collector-payments/unread/`...');
      try {
        const { data } = await axios.get('/api/v1/njangi/collector-payments/unread/')
        console.log('📥 [PaymentNotificationBadge] API Response:', data);
        if (data && data.success) {
          this.$set(this, 'unreadPayments',    data.payments      || [])
          this.$set(this, 'notificationCount', data.unread_count  || 0)
          console.log(`✨ [PaymentNotificationBadge] Loaded ${this.notificationCount} unread payouts.`);
          if (this.notificationCount > 0) {
            this.showDialog = true
          }
        } else {
          console.warn('⚠️ [PaymentNotificationBadge] API response success is false:', data);
        }
      } catch (e) {
        console.error('❌ [PaymentNotificationBadge] API request failed:', e);
      }
    },

    openDialog() {
      if (this.notificationCount > 0) this.showDialog = true
    },

    closeDialog() {
      // Cannot close manually if there are unread notifications
      if (this.notificationCount > 0) {
        this.showDialog = true
      } else {
        this.showDialog = false
      }
    },

    async acknowledgePayment() {
      if (!this.currentPayment) return
      this.acknowledging = true
      try {
        const { data } = await axios.post(
          `/api/v1/njangi/collector-payments/${this.currentPayment.id}/acknowledge/`
        )
        if (data && data.success) {
          await this.loadNotifications()
          if (this.notificationCount > 0) {
            this.showDialog = true
          } else {
            this.showDialog = false
          }
        }
      } catch (e) {
        console.error('Error acknowledging payout:', e)
      } finally {
        this.acknowledging = false
      }
    }
  },

  watch: {
    notificationCount: {
      immediate: true,
      handler(newVal) {
        if (newVal > 0) {
          this.showDialog = true
        }
      }
    }
  }
}
</script>

<style scoped>
/* ── FAB ────────────────────────────────────────────────── */
.notification-trigger-wrapper {
  position: fixed;
  bottom: 30px;
  left: 30px;
  z-index: 1050;
  display: inline-block;
}

.notification-trigger {
  width: 56px !important;
  height: 56px !important;
  min-width: 56px !important;
  box-shadow: 0 6px 20px rgba(0, 150, 136, 0.4) !important;
  transition: transform .25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow .25s ease !important;
  margin: 0 !important;
}
.notification-trigger:hover {
  transform: scale(1.1) rotate(-5deg);
  box-shadow: 0 8px 25px rgba(0, 150, 136, 0.55) !important;
}
.notification-trigger i {
  font-size: 22px !important;
  margin: 0 !important;
}

/* Badge attached directly to top-right of FAB wrapper */
.custom-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #004d40;
  color: #e0f2f1;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  z-index: 1051;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  animation: badge-pulse 2s ease-in-out infinite;
}
@keyframes badge-pulse {
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.15); }
}

/* ── Dialog hero ────────────────────────────────────────── */
.dialog-hero {
  background: linear-gradient(135deg, #00897b 0%, #004d40 60%, #1a237e 100%);
  padding: 28px 24px 24px;
  position: relative;
  overflow: hidden;
}
.dialog-hero-glow {
  position: absolute; top: -50px; right: -50px;
  width: 220px; height: 220px; border-radius: 50%;
  background: radial-gradient(circle, rgba(0,188,212,.22) 0%, transparent 70%);
}
.hero-inner { position: relative; z-index: 1; }

/* Icon circle */
.hero-icon-wrap {
  width: 64px; height: 64px; border-radius: 50%; flex-shrink: 0;
  background: rgba(255,255,255,.15);
  display: flex; align-items: center; justify-content: center;
  position: relative;
}
.hero-pulse {
  position: absolute; inset: -6px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.25);
  animation: hero-pulse 2s ease-in-out infinite;
}
@keyframes hero-pulse {
  0%, 100% { transform: scale(1); opacity: .6; }
  50%       { transform: scale(1.1); opacity: .2; }
}

/* Hero typography */
.hero-eyebrow {
  font-size: 11px; font-weight: 700;
  letter-spacing: 2px; text-transform: uppercase;
  color: rgba(255,255,255,.65); margin-bottom: 4px;
}
.hero-title {
  font-size: 22px; font-weight: 800;
  color: white; margin: 0 0 8px; line-height: 1.2;
}
.hero-chip {
  display: inline-flex; align-items: center; gap: 7px;
  background: rgba(255,255,255,.15);
  border: 1px solid rgba(255,255,255,.2);
  border-radius: 20px; padding: 3px 10px;
  font-size: 12px; color: rgba(255,255,255,.85);
}
.dot-live {
  width: 7px; height: 7px; border-radius: 50%;
  background: #00e5ff; box-shadow: 0 0 6px #00e5ff;
  animation: dot-blink 2s ease-in-out infinite; flex-shrink: 0;
}
@keyframes dot-blink { 0%,100%{opacity:1} 50%{opacity:.3} }

.hero-close {
  align-self: flex-start !important;
  margin-top: -4px !important;
}

/* ── KPI cards ──────────────────────────────────────────── */
.kpi-card {
  border-radius: 14px; padding: 14px 10px;
  text-align: center; display: flex;
  flex-direction: column; align-items: center;
}
.kpi-teal   { background: #e0f2f1; }
.kpi-green  { background: #e8f5e9; }
.kpi-purple { background: #f3e5f5; }
.kpi-label {
  display: block; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .5px;
  color: #90a4ae; margin-bottom: 3px;
}
.kpi-value {
  display: block; font-size: 14px; font-weight: 800;
  word-break: break-word; text-align: center;
}

/* ── Detail rows ────────────────────────────────────────── */
.detail-section {
  border: 1px solid #eceff1;
  border-radius: 12px; overflow: hidden;
}
.detail-row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px;
}
.detail-icon { flex-shrink: 0; }
.detail-label { font-size: 13px; color: #90a4ae; flex: 1; }
.detail-value { font-size: 13px; font-weight: 700; color: #37474f; text-align: right; }

/* ── Note box ───────────────────────────────────────────── */
.note-box {
  background: #fff8e1;
  border-left: 4px solid #f9a825;
  border-radius: 0 10px 10px 0;
  padding: 12px 16px;
  font-size: 13px; color: #5d4037; line-height: 1.6;
  display: flex; align-items: flex-start;
}

/* ── Multi-payout badge ─────────────────────────────────── */
.multi-badge {
  background: #e0f2f1; border-radius: 10px;
  padding: 10px 16px; font-size: 13px;
  color: #00695c; font-weight: 600;
  display: flex; align-items: center;
}

/* ── Actions bar ────────────────────────────────────────── */
.dialog-actions {
  background: #fafafa;
  border-top: 1px solid #eceff1;
}
.action-primary {
  border-radius: 10px !important;
  font-weight: 700 !important;
}
.action-ghost {
  border-radius: 10px !important;
}

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 600px) {
  .notification-trigger-wrapper {
    bottom: 85px;
    left: 20px;
  }
  .notification-trigger {
    width: 48px !important;
    height: 48px !important;
    min-width: 48px !important;
  }
  .notification-trigger i { font-size: 18px; }
  .hero-title { font-size: 18px; }
  .dialog-hero { padding: 20px 16px 18px; }
}
</style>